import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { PreferencesModal } from '../user/PreferencesModal';
import { PreferenceCategory } from '../../types';
import { FaEnvelope, FaCheckCircle, FaArrowRight } from 'react-icons/fa';

export default function VerifyEmail() {
  const [loading, setLoading] = useState(true);
  const [showPreferences, setShowPreferences] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email_confirmed_at) {
          // Check if user has completed their profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('zip_code, interests')
            .eq('id', session.user.id)
            .single();

          if (!profile?.zip_code || !profile?.interests) {
            navigate('/complete-profile');
          } else {
            navigate('/listings');
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [navigate]);

  const handleSavePreferences = async (preferences: PreferenceCategory[]) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          categories: preferences,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast.success('Preferences saved successfully!');
      navigate('/listings');
    } catch (error: any) {
      toast.error('Error saving preferences: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-100 dark:from-amber-900 dark:to-orange-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-orange-100 dark:bg-orange-800">
            <FaEnvelope className="h-12 w-12 text-orange-600 dark:text-orange-300" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            One Last Step!
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            We've sent a verification link to your email address.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FaCheckCircle className="h-6 w-6 text-orange-500" />
              </div>
              <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                Check your email inbox (and spam folder) for a message from HollerFinds
              </p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FaCheckCircle className="h-6 w-6 text-orange-500" />
              </div>
              <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                Click the verification link in the email to activate your account
              </p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FaCheckCircle className="h-6 w-6 text-orange-500" />
              </div>
              <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                Once verified, you'll be able to complete your profile and start exploring
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          <div className="text-center">
            <button
              onClick={() => navigate('/signin')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 dark:text-orange-300 dark:bg-orange-800 dark:hover:bg-orange-700"
            >
              Return to sign in
              <FaArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Didn't receive the email?{' '}
            <button
              onClick={() => navigate('/signin')}
              className="font-medium text-orange-600 hover:text-orange-500 dark:text-orange-400"
            >
              Try signing in again
            </button>
          </p>
        </div>
      </div>

      <PreferencesModal
        isOpen={showPreferences}
        onClose={() => {
          setShowPreferences(false);
          navigate('/listings');
        }}
        onSave={handleSavePreferences}
      />
    </div>
  );
} 